// frontend/src/pages/admin/Customers.js
import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiUserCheck,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiShield,
  FiCheckCircle,
  FiXCircle,
  FiShoppingBag,
  FiAlertCircle,
  FiCheckSquare,
  FiSquare,
  FiMoreVertical,
  FiKey // ✅ НЭМСЭН
} from 'react-icons/fi';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [customerType, setCustomerType] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [pagination, setPagination] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [showActionsMenu, setShowActionsMenu] = useState(null);

  // ✅ changePassword state нэмэх
  const [changePassword, setChangePassword] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    address: '',
    company_name: '',
    tax_number: '',
    customer_type: 'retail',
    discount_rate: 0,
    credit_limit: 0,
    payment_terms: 0,
    tax_exempt: false,
    is_active: true,
    verified: false,
    notes: ''
  });

  const [permissions, setPermissions] = useState({});
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    fetchCustomers();
  }, [searchQuery, customerType, verifiedFilter]);

  const fetchCustomers = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/customers', {
        params: {
          page,
          limit: 20,
          search: searchQuery,
          customer_type: customerType,
          verified: verifiedFilter
        }
      });

      if (response.data.success) {
        setCustomers(response.data.data.customers);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Fetch customers error:', error);
      toast.error('Харилцагчийн жагсаалт татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setChangePassword(false); // ✅ RESET
    setFormData({
      email: '',
      password: '',
      full_name: '',
      phone: '',
      address: '',
      company_name: '',
      tax_number: '',
      customer_type: 'retail',
      discount_rate: 0,
      credit_limit: 0,
      payment_terms: 0,
      tax_exempt: false,
      is_active: true,
      verified: false,
      notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (customer) => {
    setModalMode('edit');
    setSelectedCustomer(customer);
    setChangePassword(false); // ✅ RESET
    setFormData({
      email: customer.email,
      password: '', // ✅ Хоосон
      full_name: customer.full_name,
      phone: customer.phone || '',
      address: customer.address || '',
      company_name: customer.company_name || '',
      tax_number: customer.tax_number || '',
      customer_type: customer.customer_type || 'retail',
      discount_rate: customer.discount_rate || 0,
      credit_limit: customer.credit_limit || 0,
      payment_terms: customer.payment_terms || 0,
      tax_exempt: customer.tax_exempt || false,
      is_active: customer.is_active,
      verified: customer.verified,
      notes: customer.notes || ''
    });
    setShowModal(true);
    setShowActionsMenu(null);
  };

  const openPermissionsModal = async (customer) => {
    setModalMode('permissions');
    setSelectedCustomer(customer);
    setLoading(true);

    try {
      const response = await axios.get(`/api/customers/${customer.id}/permissions`);
      if (response.data.success) {
        setPermissions(response.data.data.permissions);

        const granted = [];
        Object.values(response.data.data.permissions).forEach(category => {
          category.forEach(perm => {
            if (perm.granted) {
              granted.push(perm.id);
            }
          });
        });
        setSelectedPermissions(granted);
      }
    } catch (error) {
      console.error('Fetch permissions error:', error);
      toast.error('Эрхүүдийн жагсаалт татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }

    setShowModal(true);
    setShowActionsMenu(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (modalMode === 'create') {
        const response = await axios.post('/api/customers', formData);
        if (response.data.success) {
          toast.success('Харилцагч амжилттай бүртгэгдлээ');
          fetchCustomers();
          setShowModal(false);
        }
      } else if (modalMode === 'edit') {
        // ✅ Нууц үг солихгүй бол password-ийг илгээхгүй
        const dataToSend = { ...formData };
        if (!changePassword) {
          delete dataToSend.password;
        }

        const response = await axios.put(`/api/customers/${selectedCustomer.id}`, dataToSend);
        if (response.data.success) {
          toast.success(response.data.message);
          fetchCustomers();
          setShowModal(false);
        }
      } else if (modalMode === 'permissions') {
        const response = await axios.put(`/api/customers/${selectedCustomer.id}/permissions`, {
          permissions: selectedPermissions
        });
        if (response.data.success) {
          toast.success('Эрхүүд шинэчлэгдлээ');
          setShowModal(false);
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (id) => {
    if (!window.confirm('Энэ харилцагчийг баталгаажуулах уу?')) return;

    setLoading(true);
    try {
      const response = await axios.put(`/api/customers/${id}/verify`);
      if (response.data.success) {
        toast.success('Харилцагч баталгаажлаа');
        fetchCustomers();
      }
    } catch (error) {
      console.error('Verify error:', error);
      toast.error('Баталгаажуулахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
    setShowActionsMenu(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('⚠️ Харилцагчийг устгах уу?')) return;

    setLoading(true);
    try {
      const response = await axios.delete(`/api/customers/${id}`);
      if (response.data.success) {
        toast.success('Харилцагч устгагдлаа');
        setCustomers(prevCustomers => prevCustomers.filter(c => c.id !== id));
        setPagination(prev => ({
          ...prev,
          total: prev.total - 1
        }));
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
    setShowActionsMenu(null);
  };

  const togglePermission = (permId) => {
    if (selectedPermissions.includes(permId)) {
      setSelectedPermissions(selectedPermissions.filter(id => id !== permId));
    } else {
      setSelectedPermissions([...selectedPermissions, permId]);
    }
  };

  const toggleCategoryPermissions = (category) => {
    const categoryPerms = permissions[category] || [];
    const categoryPermIds = categoryPerms.map(p => p.id);

    const allSelected = categoryPermIds.every(id => selectedPermissions.includes(id));

    if (allSelected) {
      setSelectedPermissions(selectedPermissions.filter(id => !categoryPermIds.includes(id)));
    } else {
      setSelectedPermissions([...new Set([...selectedPermissions, ...categoryPermIds])]);
    }
  };

  const categoryLabels = {
    products: '📦 Бараа модуль',
    orders: '📋 Захиалга модуль'
  };

  return (
    <div className="w-full h-full overflow-hidden">
      <div className="p-3 sm:p-4 lg:p-6 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
              <FiUserCheck className="mr-2 flex-shrink-0" size={20} />
              <span className="truncate">Харилцагчийн удирдлага</span>
            </h1>
            <p className="text-gray-600 mt-1 text-xs sm:text-sm truncate">
              Харилцагч бүртгэх, баталгаажуулах, эрх олгох
            </p>
          </div>
          <button 
            onClick={openCreateModal} 
            className="btn btn-primary whitespace-nowrap flex-shrink-0 text-sm"
          >
            <FiPlus className="mr-1" size={16} />
            <span className="hidden sm:inline">Шинэ харилцагч</span>
            <span className="sm:hidden">Нэмэх</span>
          </button>
        </div>

        {/* Filters */}
        <div className="card mb-4 p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="text-gray-400" size={16} />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Нэр, и-мэйл, утас..."
                  className="input pl-9 text-sm"
                />
              </div>
            </div>

            <div>
              <select
                value={customerType}
                onChange={(e) => setCustomerType(e.target.value)}
                className="input text-sm"
              >
                <option value="">Бүх төрөл</option>
                <option value="retail">Жижиглэн</option>
                <option value="wholesale">Бөөний</option>
              </select>
            </div>

            <div>
              <select
                value={verifiedFilter}
                onChange={(e) => setVerifiedFilter(e.target.value)}
                className="input text-sm"
              >
                <option value="">Бүх төлөв</option>
                <option value="true">Баталгаажсан</option>
                <option value="false">Хүлээгдэж байгаа</option>
              </select>
            </div>
          </div>
        </div>

        {/* Customers Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">ID</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Харилцагч</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap hidden lg:table-cell">Компани</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap hidden xl:table-cell">Утас</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Төрөл</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap hidden md:table-cell">Төлөв</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 whitespace-nowrap">Үйлдэл</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-12">
                      <div className="spinner-lg mx-auto mb-3"></div>
                      <p className="text-gray-500 text-sm">Уншиж байна...</p>
                    </td>
                  </tr>
                ) : customers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-12 text-gray-500">
                      <FiUserCheck size={40} className="mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Харилцагч олдсонгүй</p>
                    </td>
                  </tr>
                ) : (
                  customers.map(customer => (
                    <tr key={customer.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-3">
                        <span className="font-mono text-xs text-gray-600">#{customer.id}</span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center min-w-0">
                          <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                            <span className="text-white font-bold text-sm">
                              {customer.full_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-gray-900 text-sm truncate">{customer.full_name}</p>
                            <p className="text-xs text-gray-500 truncate">{customer.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 hidden lg:table-cell">
                        <p className="text-sm text-gray-900 truncate max-w-[150px]">{customer.company_name || '-'}</p>
                        {customer.tax_number && (
                          <p className="text-xs text-gray-500 truncate">НӨАТ: {customer.tax_number}</p>
                        )}
                      </td>
                      <td className="px-3 py-3 hidden xl:table-cell">
                        <p className="text-sm text-gray-600">{customer.phone || '-'}</p>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          <span className={`badge text-xs px-2 py-1 ${
                            customer.customer_type === 'wholesale' ? 'badge-blue' : 'badge-green'
                          }`}>
                            {customer.customer_type === 'wholesale' ? '🏢' : '🛒'}
                          </span>
                          {!customer.verified && (
                            <span className="badge badge-warning text-xs px-2 py-1">
                              <FiAlertCircle size={10} />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 hidden md:table-cell">
                        <div className="flex flex-col gap-1">
                          {customer.verified ? (
                            <span className="badge badge-success text-xs px-2 py-0.5 inline-flex items-center w-fit">
                              <FiCheckCircle className="mr-1" size={10} />
                              Баталгаажсан
                            </span>
                          ) : (
                            <span className="badge badge-warning text-xs px-2 py-0.5 inline-flex items-center w-fit">
                              <FiAlertCircle className="mr-1" size={10} />
                              Хүлээгдэж байна
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="relative inline-block">
                          <button
                            onClick={() => setShowActionsMenu(showActionsMenu === customer.id ? null : customer.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Үйлдлүүд"
                          >
                            <FiMoreVertical size={16} className="text-gray-600" />
                          </button>

                          {/* Dropdown Menu */}
                          {showActionsMenu === customer.id && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setShowActionsMenu(null)}
                              ></div>
                              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                                {!customer.verified && (
                                  <button
                                    onClick={() => handleVerify(customer.id)}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 flex items-center"
                                  >
                                    <FiCheckCircle className="mr-2" size={14} />
                                    Баталгаажуулах
                                  </button>
                                )}
                                <button
                                  onClick={() => openPermissionsModal(customer)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center"
                                >
                                  <FiShield className="mr-2" size={14} />
                                  Эрх олгох
                                </button>
                                <button
                                  onClick={() => openEditModal(customer)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-yellow-50 flex items-center"
                                >
                                  <FiEdit2 className="mr-2" size={14} />
                                  Засах
                                </button>
                                <button
                                  onClick={() => handleDelete(customer.id)}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center border-t border-gray-100"
                                >
                                  <FiTrash2 className="mr-2" size={14} />
                                  Устгах
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {pagination.total} харилцагч
              </div>
              <div className="flex gap-2">
                {[...Array(pagination.pages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => fetchCustomers(i + 1)}
                    className={`px-3 py-1 rounded text-sm font-medium transition ${
                      pagination.page === i + 1
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b bg-gradient-to-r from-green-50 to-emerald-50">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                {modalMode === 'create' && '✨ Шинэ харилцагч бүртгэх'}
                {modalMode === 'edit' && '✏️ Харилцагч засах'}
                {modalMode === 'permissions' && '🔐 Эрх олгох'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {modalMode !== 'permissions' ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          👤 Овог нэр <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.full_name}
                          onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                          className="input"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          ✉️ И-мэйл <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="input"
                          required
                        />
                      </div>
                    </div>

                    {/* ✅ Нууц үг хэсэг - CREATE болон EDIT-д */}
                    {modalMode === 'create' ? (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          🔒 Нууц үг <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          className="input"
                          required
                          minLength={6}
                        />
                      </div>
                    ) : (
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            checked={changePassword}
                            onChange={(e) => {
                              setChangePassword(e.target.checked);
                              if (!e.target.checked) {
                                setFormData({...formData, password: ''});
                              }
                            }}
                            className="mt-1 w-5 h-5 text-blue-600"
                          />
                          <div className="ml-3 flex-1">
                            <span className="text-sm font-bold text-gray-900 flex items-center gap-2">
                              <FiKey size={16} />
                              Нууц үг шинэчлэх
                            </span>
                            <p className="text-xs text-gray-600 mt-1">
                              Харилцагчийн нууц үгийг шинэчлэх бол энэ сонголтыг идэвхжүүлнэ үү
                            </p>
                          </div>
                        </label>

                        {changePassword && (
                          <div className="mt-3">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              🔒 Шинэ нууц үг <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="password"
                              value={formData.password}
                              onChange={(e) => setFormData({...formData, password: e.target.value})}
                              className="input"
                              required={changePassword}
                              minLength={6}
                              placeholder="Шинэ нууц үг оруулна уу"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          📞 Утас
                        </label>
                        <input
                          type="text"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="input"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          🏢 Компанийн нэр
                        </label>
                        <input
                          type="text"
                          value={formData.company_name}
                          onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                          className="input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          🏷️ НӨАТ дугаар
                        </label>
                        <input
                          type="text"
                          value={formData.tax_number}
                          onChange={(e) => setFormData({...formData, tax_number: e.target.value})}
                          className="input"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          📦 Харилцагчийн төрөл
                        </label>
                        <select
                          value={formData.customer_type}
                          onChange={(e) => setFormData({...formData, customer_type: e.target.value})}
                          className="input"
                        >
                          <option value="retail">Жижиглэн</option>
                          <option value="wholesale">Бөөний</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          💡 Үнэ нь тоо хэмжээнээс хамаарна
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          💰 Хөнгөлөлт (%)
                        </label>
                        <input
                          type="number"
                          value={formData.discount_rate}
                          onChange={(e) => setFormData({...formData, discount_rate: parseFloat(e.target.value) || 0})}
                          className="input"
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          💳 Зээлийн лимит (₮)
                        </label>
                        <input
                          type="number"
                          value={formData.credit_limit}
                          onChange={(e) => setFormData({...formData, credit_limit: parseFloat(e.target.value) || 0})}
                          className="input"
                          min="0"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          📅 Төлбөрийн хугацаа (өдөр)
                        </label>
                        <input
                          type="number"
                          value={formData.payment_terms}
                          onChange={(e) => setFormData({...formData, payment_terms: parseInt(e.target.value) || 0})}
                          className="input"
                          min="0"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        📍 Хаяг
                      </label>
                      <textarea
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        className="input"
                        rows="2"
                      ></textarea>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        📝 Тэмдэглэл
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="input"
                        rows="2"
                      ></textarea>
                    </div>

                    {/* ✅ НӨАТ-ын хөнгөлөлт */}
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.tax_exempt}
                          onChange={(e) => setFormData({...formData, tax_exempt: e.target.checked})}
                          className="mt-1 w-5 h-5 text-yellow-600"
                        />
                        <div className="ml-3 flex-1">
                          <span className="text-sm font-bold text-gray-900">
                            🏷️ НӨАТ-аас чөлөөлөх
                          </span>
                          <p className="text-xs text-gray-600 mt-1">
                            Идэвхжүүлбэл энэ харилцагчийн худалдан авалт дээр НӨАТ бодогдохгүй (10% хасагдана)
                          </p>
                        </div>
                      </label>
                    </div>

                    {/* ✅ Идэвхтэй, Баталгаажсан - НЭГ Л УДАА */}
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                          className="w-5 h-5 text-green-600"
                        />
                        <span className="ml-2 text-sm font-medium">✅ Идэвхтэй</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.verified}
                          onChange={(e) => setFormData({...formData, verified: e.target.checked})}
                          className="w-5 h-5 text-green-600"
                        />
                        <span className="ml-2 text-sm font-medium">✅ Баталгаажсан</span>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                      <p className="text-sm text-blue-800">
                        <strong>💡 Санамж:</strong> Харилцагчид зөвхөн бараа харах болон захиалга өгөх эрхүүд байна.
                      </p>
                    </div>

                    {Object.entries(permissions).map(([category, perms]) => {
                      const categoryPermIds = perms.map(p => p.id);
                      const allCategorySelected = categoryPermIds.every(id => selectedPermissions.includes(id));

                      return (
                        <div key={category} className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 border-b flex items-center justify-between">
                            <h4 className="font-bold text-gray-900">
                              {categoryLabels[category] || category}
                            </h4>
                            <button
                              type="button"
                              onClick={() => toggleCategoryPermissions(category)}
                              className="text-sm px-3 py-1.5 bg-white border rounded-lg hover:bg-gray-50"
                            >
                              {allCategorySelected ? '❌ Бүгдийг хасах' : '✅ Бүгдийг сонгох'}
                            </button>
                          </div>

                          <div className="p-3 space-y-2">
                            {perms.map(perm => (
                              <label
                                key={perm.id}
                                className="flex items-start cursor-pointer hover:bg-blue-50 p-3 rounded-lg transition"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedPermissions.includes(perm.id)}
                                  onChange={() => togglePermission(perm.id)}
                                  className="mt-1 mr-3 w-5 h-5 text-blue-600"
                                />
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900">{perm.name}</p>
                                  <p className="text-sm text-gray-500">{perm.description}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 p-4 sm:p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 sm:px-6 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Болих
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 sm:px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 text-sm"
                >
                  {loading ? 'Хадгалж байна...' : '💾 Хадгалах'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;